import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { getNewsletterPerformance } from "@/lib/newsletter-performance.functions";

export const Route = createFileRoute("/admin/newsletter-performance")({
  head: () => ({
    meta: [
      { title: "Bülten Performansı – Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: NewsletterPerformancePage,
});

function pct(num: number, den: number) {
  if (!den) return "—";
  return `${((num / den) * 100).toFixed(1)}%`;
}

function fmtDate(s: string) {
  try {
    return new Date(s).toLocaleString("tr-TR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return s;
  }
}

function templateLabel(t: string) {
  if (t === "newsletter") return "Bülten";
  if (t === "book_announcement") return "Kitap duyurusu";
  if (t === "signal-briefing") return "Signal Briefing";
  return t;
}

function NewsletterPerformancePage() {
  const fetcher = useServerFn(getNewsletterPerformance);
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["newsletter-performance"],
    queryFn: () => fetcher(),
    staleTime: 0,
    refetchInterval: 30_000,
    refetchOnWindowFocus: "always",
  });

  const campaigns = data?.campaigns ?? [];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif text-3xl mb-1">Bülten Performansı</h1>
          <p className="text-sm text-ink/60">
            Gönderilen her kampanya için teslim, açılma ve tıklama oranları.
          </p>
          {data?.refreshed_at && (
            <p className="text-[11px] text-ink/45 mt-1" role="status">
              Son güncelleme: {fmtDate(data.refreshed_at)} · Her 30 saniyede yenilenir
            </p>
          )}
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="text-xs uppercase tracking-[0.2em] border border-ink/30 px-3 py-2 hover:bg-ink hover:text-paper disabled:opacity-50"
        >
          {isFetching ? "Yükleniyor…" : "Yenile"}
        </button>
      </div>

      {isLoading && (
        <div className="text-sm text-ink/60">Veriler yükleniyor…</div>
      )}

      {error && (
        <div className="text-sm text-clay">
          Hata: {(error as Error).message}
        </div>
      )}

      {!isLoading && campaigns.length === 0 && (
        <div className="text-sm text-ink/60 border border-ink/10 p-6 rounded">
          Henüz takip edilebilen bir kampanya yok. Bülten gönderildikten sonra
          burada teslim / açılma / tıklama verilerini göreceksiniz.
        </div>
      )}

      {campaigns.length > 0 && (
        <div className="space-y-3">
          <div className="text-[11px] uppercase tracking-[0.18em] text-ink/50 mb-2">
            Not: Açılma sayıları, Apple Mail Privacy ve Gmail görüntü ön-yükleme
            yüzünden gerçekten daha yüksek görünebilir. Tıklama oranı daha
            güvenilir bir sinyaldir.
          </div>
          <div className="overflow-x-auto border border-ink/10 rounded">
            <table className="w-full text-sm">
              <thead className="bg-ink/5 text-left">
                <tr>
                  <th className="px-3 py-2 font-medium">Konu</th>
                  <th className="px-3 py-2 font-medium">Tür</th>
                  <th className="px-3 py-2 font-medium">Tarih</th>
                  <th className="px-3 py-2 font-medium text-right">Alıcı</th>
                  <th className="px-3 py-2 font-medium text-right">Gönderildi</th>
                  <th className="px-3 py-2 font-medium text-right">Açılma</th>
                  <th className="px-3 py-2 font-medium text-right">Açılma %</th>
                  <th className="px-3 py-2 font-medium text-right">Tıklama</th>
                  <th className="px-3 py-2 font-medium text-right">CTR</th>
                  <th className="px-3 py-2 font-medium text-right">Hata</th>
                  <th className="px-3 py-2 font-medium text-right">Çıkış</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c) => (
                  <tr key={c.campaign_id} className="border-t border-ink/10 align-top">
                    <td className="px-3 py-3">
                      <div className="font-medium">{c.subject}</div>
                      <div className="text-[11px] text-ink/40 font-mono">
                        {c.campaign_id.slice(0, 8)}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-ink/70">
                      {templateLabel(c.template_name)}
                    </td>
                    <td className="px-3 py-3 text-ink/70 whitespace-nowrap">
                      {fmtDate(c.sent_at)}
                    </td>
                    <td className="px-3 py-3 text-right">{c.recipients}</td>
                    <td className="px-3 py-3 text-right text-emerald-700">
                      {c.sent}
                    </td>
                    <td className="px-3 py-3 text-right">
                      {c.opened}
                      <span className="text-ink/40 text-[11px]"> ({c.open_count})</span>
                    </td>
                    <td className="px-3 py-3 text-right">{pct(c.opened, c.sent)}</td>
                    <td className="px-3 py-3 text-right">
                      {c.clicked}
                      <span className="text-ink/40 text-[11px]"> ({c.click_count})</span>
                    </td>
                    <td className="px-3 py-3 text-right">{pct(c.clicked, c.sent)}</td>
                    <td className="px-3 py-3 text-right text-clay">{c.failed || ""}</td>
                    <td className="px-3 py-3 text-right text-ink/70">
                      {c.unsubscribed || ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-[11px] text-ink/50 mt-3 space-y-1">
            <div>• <strong>Açılma</strong>: en az bir kez piksel yüklenen alıcı sayısı (parantez içinde toplam açılma olayı).</div>
            <div>• <strong>Tıklama / CTR</strong>: en az bir link tıklayan alıcı / gönderilen.</div>
            <div>• <strong>Çıkış</strong>: kampanya gönderildikten sonra suppression listesine düşen alıcılar (unsubscribe, bounce, complaint).</div>
          </div>
        </div>
      )}
    </div>
  );
}
